import React, { useRef, useState, useEffect } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import { useLocation, useParams, useSearchParams } from 'react-router-dom';
import { PDFDocument, StandardFonts } from 'pdf-lib';
import { getAllDetails, getSpecificDetails, postAllData, updateData } from '../../service/ApiService';
import {
  GET_CURRENT_DATE_TIME,
  GET_DOCUMENT_TO_BE_SIGNED,
  UPLOAD_SIGNED_DOC,
  GET_DOCUMENT_FOR_SIGN,
  UPLOAD_COMPANY_SIGNED_TAX_FILING_DOC
} from '../../config/ApiConstant';
import { Toast } from 'primereact/toast';
import {
  PenTool,
  Type,
  X,
  CheckCircle,
  Eye,
  Save,
  Edit3,
  RefreshCw,
} from 'lucide-react';
import Loader from '../../components/Loader/Loader';

// --- Configuration ---

const SignatureLayout = {
  DRAW_MODE: { x: 50, width: 60, height: 24, y: 255 },
  TYPE_MODE: { x: 50, width: 150, height: 60, y: 235 },
};

const DateStampLayout = {
  START_X: 367,
  Y_POSITION: 238,
  GAP: 50,
};

const CompanySignatureLayout = {
  PAGE_INDEX: 1, // Second page for T183 CORP Part 3
  DRAW_MODE: { x: 105, y: 590, width: 100, height: 20, },
  TYPE_MODE: { x: 90, y: 575, width: 180, height: 40, },
};

const CompanyDateStampLayout = {
  PAGE_INDEX: 1,
  START_X: 510,
  DATE_Y: 590,
  TIME_Y: 560,
};

const Signdoc: React.FC = () => {
  // --- Refs & Hooks ---
  const location = useLocation();
  const toast = useRef<any>(null);
  const [searchParams] = useSearchParams();
  const type = searchParams.get('type')?.toLowerCase();

  // document id and taxpayer id
  const { id, taxformId }: any = useParams();
  const docId: any = id ? atob(id) : null;
  const realTaxformId: any = taxformId ? atob(taxformId) : null;

  // signature ref
  const signaturePadRef = useRef<SignatureCanvas | null>(null);
  const pdfContainerRef = useRef<HTMLDivElement>(null);

  // --- State Variables ---
  const [documentId, setDocumentId] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isProcessing, setIsProcessing] = useState<boolean>(false);

  const [signPDFData, setSignPDFData] = useState<any>({});

  // Signature State
  const [placedSignatures, setPlacedSignatures] = useState<
    { x: number | string; y: number | string; src: string; type?: string }[]
  >([]);
  const [signedPdf, setSignedPdf] = useState<File | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [previewPdfUrl, setPreviewPdfUrl] = useState<string | null>(null);

  // Input Choice: 'draw' or 'type'
  const [inputMethod, setInputMethod] = useState<'draw' | 'type'>('draw');
  const [typedSignatureText, setTypedSignatureText] = useState<string>('');

  // Height of the PDF container (defaulted to full viewport)
  const [pageHeight, setPageHeight] = useState<number | string>('100%');

  // --- Effects ---

  // Load Font for "Type" mode preview
  useEffect(() => {
    const fontLink = document.createElement('link');
    fontLink.href =
      'https://fonts.googleapis.com/css2?family=Allura&display=swap';
    fontLink.rel = 'stylesheet';
    document.head.appendChild(fontLink);
  }, []);

  // Load PDF to calculate height (optional logic)
  useEffect(() => {
    const calculatePdfHeight = async () => {
      try {
        let fileBuffer;
        if (selectedFile) {
          fileBuffer = await selectedFile.arrayBuffer();
        } else if (previewUrl) {
          const res = await fetch(previewUrl);
          fileBuffer = await res.arrayBuffer();
        }

        if (fileBuffer) {
          const pdf = await PDFDocument.load(fileBuffer);
          const pages = pdf.getPages();
          let totalHeight = 0;
          for (const page of pages) {
            const { height } = page.getSize();
            totalHeight += height;
          }
          setPageHeight(totalHeight * 1);
        }
      } catch (error) {
        console.warn('Could not calculate PDF height:', error);
        setPageHeight(1080);
      }
    };
    if (previewUrl) calculatePdfHeight();
  }, [previewUrl, selectedFile]);

  // api call to get document to be signed
  const getDocumentToBeSigned = async () => {
    if (!docId) return;
    try {
      setDocumentId(docId); // Ensure the UI switches to signing mode
      const apiEndpoint = type === 'company' ? GET_DOCUMENT_FOR_SIGN : GET_DOCUMENT_TO_BE_SIGNED;
      const response: any = await getSpecificDetails(
        apiEndpoint,
        docId,
      );
      setSignPDFData(response);
      setPreviewUrl(response.fileUrl);
    } catch (error: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: error.response?.data?.message,
      });
    }
  };

  // api call to get company document to be signed
  const getCompanySignatureData = async () => {
    // For companies, we use the first parameter
    if (!docId) return;
    try {
      setDocumentId(docId);
      const response: any = await getSpecificDetails(
        GET_DOCUMENT_FOR_SIGN,
        docId,
      );

      // Handle the specific company data structure
      let companyData = response?.data || response;

      // If it's a list (as in logs), take the first item
      if (Array.isArray(companyData)) {
        companyData = companyData[0];
      }

      setSignPDFData(companyData);

      // Extract URL from fileUrl or documents array
      const fileUrl = companyData.fileUrl || (companyData.documents && companyData.documents[0]?.doc_url);
      setPreviewUrl(fileUrl);
    } catch (error: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: error.response?.data?.message || 'Failed to fetch company document',
      });
    }
  };

  useEffect(() => {
    if (docId) {
      setDocumentId(docId);
      if (type === 'company') {
        getCompanySignatureData();
      } else {
        getDocumentToBeSigned();
      }
    }
  }, [docId, type]);

  // get typed signature image or drawn signature image
  const generateTextSignatureImage = (text: string) => {
    const canvas = document.createElement('canvas');
    canvas.width = 500;
    canvas.height = 200;
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.font = '80px "Allura", cursive';
      ctx.fillStyle = 'black';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(text, 250, 100);
      return canvas.toDataURL('image/png');
    }
    return '';
  };

  // get signed pdf after signature
  useEffect(() => {
    const updateSignedPdf = async () => {
      if (placedSignatures.length > 0) {
        try {
          const pdfBytes: any = await generateSignedPdf();
          if (pdfBytes) {
            const blob = new Blob([pdfBytes], { type: 'application/pdf' });
            const file = new File([blob], 'signed_document.pdf', {
              type: 'application/pdf',
            });
            setSignedPdf(file);

            // Automatically load preview with signature and time/date
            const url = URL.createObjectURL(blob);
            setPreviewPdfUrl(url);
          }
        } catch (e) {
          toast.current?.show({
            severity: 'error',
            summary: 'Error',
            detail: 'Error auto-generating PDF',
          });
        }
      } else {
        setSignedPdf(null);
        setPreviewPdfUrl(null);
      }
    };
    updateSignedPdf();
  }, [placedSignatures]);

  // generate signed pdf
  const generateSignedPdf = async () => {
    if ((!selectedFile && !previewUrl) || placedSignatures.length === 0) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Missing file or signature',
      });
      return false;
    }

    // Load Original PDF
    try {
      let originalPdfBytes;
      if (selectedFile) {
        originalPdfBytes = await selectedFile.arrayBuffer();
      } else if (previewUrl) {
        const res = await fetch(previewUrl);
        if (!res.ok) {
          toast.current?.show({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to fetch PDF from URL',
          });
          return false;
        }
        originalPdfBytes = await res.arrayBuffer();
      }

      if (!originalPdfBytes) {
        toast.current?.show({
          severity: 'error',
          summary: 'Error',
          detail: 'Could not load PDF data',
        });
        return false;
      }

      const pdfDoc = await PDFDocument.load(originalPdfBytes);
      const pages = pdfDoc.getPages();

      // Determine Page and Layout based on type
      const isCompany = type === 'company';
      const targetPageIndex = isCompany ? CompanySignatureLayout.PAGE_INDEX : 0;

      // Safety check for page existence
      if (pages.length <= targetPageIndex) {
        console.warn(`Target page index ${targetPageIndex} not found, falling back to first page.`);
      }
      const targetPage = pages[pages.length > targetPageIndex ? targetPageIndex : 0];

      // Embed Signature Image
      const signatureData = placedSignatures[0];
      let imageBytes: Uint8Array;
      if (signatureData.src.startsWith('data:')) {
        const baseData = signatureData.src.split(',')[1];
        const binaryString = atob(baseData);
        imageBytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          imageBytes[i] = binaryString.charCodeAt(i);
        }
      } else {
        imageBytes = await fetch(signatureData.src)
          .then((res) => res.arrayBuffer())
          .then((buffer) => new Uint8Array(buffer));
      }

      const embeddedImage = await pdfDoc.embedPng(imageBytes);

      // 4. Draw Signature
      const isDrawMode = signatureData.type === 'draw';
      const signLayout = isDrawMode
        ? (isCompany ? CompanySignatureLayout.DRAW_MODE : SignatureLayout.DRAW_MODE)
        : (isCompany ? CompanySignatureLayout.TYPE_MODE : SignatureLayout.TYPE_MODE);

      targetPage.drawImage(embeddedImage, {
        x: signLayout.x,
        y: signLayout.y,
        width: signLayout.width,
        height: signLayout.height,
      });

      // 5. Add Date & Time Stamp
      let date = "";
      let time = "";
      try {
        const response = await getAllDetails(GET_CURRENT_DATE_TIME);
        if (response?.time) {
          date = response?.date;
          time = response?.time;
        }
      } catch (error) {
        console.warn('Failed to fetch server time');
      }

      const font = await pdfDoc.embedFont(StandardFonts.Helvetica);

      if (isCompany) {
        // Print Date for Company (Page 2)
        targetPage.drawText(date, {
          x: CompanyDateStampLayout.START_X,
          y: CompanyDateStampLayout.DATE_Y,
          size: 10,
          font,
        });

        // Print Time for Company (Below Date)
        targetPage.drawText(time, {
          x: CompanyDateStampLayout.START_X,
          y: CompanyDateStampLayout.TIME_Y,
          size: 10,
          font,
        });
      } else {
        const dateWidth = font.widthOfTextAtSize(date, 10);
        // Print Date for Individual
        targetPage.drawText(date, {
          x: DateStampLayout.START_X,
          y: DateStampLayout.Y_POSITION,
          size: 10,
          font,
        });

        // Print Time for Individual (with gap)
        targetPage.drawText(time, {
          x: DateStampLayout.START_X + dateWidth + DateStampLayout.GAP,
          y: DateStampLayout.Y_POSITION,
          size: 10,
          font,
        });
      }

      // 6. Return PDF Bytes
      return await pdfDoc.save();
    } catch (error: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: error?.message || 'Error occurred while generating PDF',
      });
      console.error('PDF Generation Error:', error);
      return false;
    }
  };

  // save signature on document
  const saveSignature = async () => {
    let signatureImage: string | null | undefined = null;

    if (inputMethod === 'draw') {
      if (signaturePadRef.current?.isEmpty())
        return toast.current?.show({
          severity: 'error',
          summary: 'Error',
          detail: 'Please sign first',
        });
      signatureImage = signaturePadRef.current
        ?.getTrimmedCanvas()
        .toDataURL('image/png');
    } else {
      if (!typedSignatureText.trim())
        return toast.current?.show({
          severity: 'error',
          summary: 'Error',
          detail: 'Please type your name',
        });
      signatureImage = generateTextSignatureImage(typedSignatureText);
    }

    if (signatureImage) {
      setIsProcessing(true);
      setTimeout(() => {
        setPlacedSignatures([
          {
            x: '15%',
            y: '80%',
            src: signatureImage!,
            type: inputMethod,
          },
        ]);
        toast.current?.show({
          severity: 'success',
          summary: 'Success',
          detail: 'Signature placed!',
        });
        setIsProcessing(false);
        setIsModalOpen(false);
      }, 500);
    }
  };

  const clearSignaturePad = () => {
    signaturePadRef.current?.clear();
    setTypedSignatureText('');
  };

  const onFinish = async () => {
    let fileToUpload = signedPdf;
    if (!fileToUpload) {
      if (placedSignatures.length === 0) return;
      const pdfBytes: any = await generateSignedPdf();
      if (!pdfBytes) return;

      const blob = new Blob([pdfBytes], { type: 'application/pdf' });
      fileToUpload = new File([blob], 'signed_document.pdf', {
        type: 'application/pdf',
      });
    }

    if (type === 'company') {
      await uploadCompanySdk(fileToUpload);
    } else {
      await uploadSdk(fileToUpload);
    }
  };

  const onPreview = async () => {
    if (!signedPdf) {
      if (placedSignatures.length === 0) return;

      const pdfBytes: any = await generateSignedPdf();
      if (!pdfBytes) return;

      const blob = new Blob([pdfBytes], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      setPreviewPdfUrl(url);
      return;
    }
    const url = URL.createObjectURL(signedPdf);
    setPreviewPdfUrl(url);
  };

  // on finish api call
  const uploadSdk = async (file: File) => {
    setIsProcessing(true);
    try {
      // Upload Logic
      const formData = new FormData();
      formData.append('file', file);
      formData.append('id', docId);

      const response = await postAllData(UPLOAD_SIGNED_DOC, formData);
      if (response) {
        toast.current?.show({
          severity: 'success',
          summary: 'Success',
          detail: response?.message,
        });
        getDocumentToBeSigned();
      }
    } catch (error: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: error.response?.data?.message,
      });
    }
    setIsProcessing(false);
  };

  // Dedicated upload logic for company documents
  const uploadCompanySdk = async (file: File) => {
    setIsProcessing(true);
    try {
      // Upload Logic
      const formData = new FormData();
      formData.append('file', file);

      // Using updateData for companies as it appends the ID to the URL and matches the backoffice pattern
      const response = await updateData(UPLOAD_COMPANY_SIGNED_TAX_FILING_DOC, docId, formData);

      if (response) {
        toast.current?.show({
          severity: 'success',
          summary: 'Success',
          detail: response?.message,
        });

        // Use response data immediately if available as a fallback for re-fetch failures
        const updatedData = response?.data || response;
        if (updatedData && typeof updatedData === 'object') {
          setSignPDFData(updatedData);
          setPreviewUrl(updatedData.fileUrl || updatedData.doc_url || previewUrl);
        }

        try {
          await getCompanySignatureData();
        } catch (fetchError) {
          console.warn('Re-fetch failed, staying on current data state');
        }
      }
    } catch (error: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: error.response?.data?.message || 'Failed to upload company signature',
      });
    }
    setIsProcessing(false);
  };

  // Signing Screen
  return (
    <>
      <style>{globalStylesText}</style>
      <Toast ref={toast} />
      {previewUrl || signPDFData?.show_document ? (
        <>
          {signPDFData?.show_document == true ? (
            <div style={styles.container}>
              <div style={styles.header}>
                <h2 style={styles.title}>
                  {previewPdfUrl ? 'Preview Document' : 'Sign Document'}
                </h2>
                <p style={styles.subtitle}>
                  Please review the document below and provide your signature.
                </p>
              </div>

              <div style={styles.contentWrapper}>
                <div
                  style={{ ...styles.pdfCol, margin: '0 auto', maxWidth: '900px' }}
                >
                  {previewUrl ? (
                    <div
                      style={{ ...styles.pdfWrapper, height: `${pageHeight}px` }}
                      ref={pdfContainerRef}
                    >
                      <iframe
                        src={`${previewPdfUrl || previewUrl
                          }#toolbar=0&navpanes=0&scrollbar=1`}
                        style={{
                          ...styles.iframe,
                          overflow: 'scroll',
                          pointerEvents: 'all',
                        }}
                        title="PDF Preview"
                      />
                    </div>
                  ) : (
                    <div style={styles.loaderContainer}>
                      <RefreshCw style={styles.spinner} size={24} />
                      <p>Loading Document...</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Action Panel: Fixed at bottom */}
              <div style={styles.actionPanel}>
                {/* Button: Open Sign Modal */}
                {previewUrl && !previewPdfUrl && (
                  <button
                    onClick={() => setIsModalOpen(true)}
                    style={styles.primaryBtn}
                  >
                    <PenTool size={18} />
                    {placedSignatures.length > 0 ? 'Sign Again' : 'Sign Here'}
                  </button>
                )}

                {/* Button: Preview Signed PDF */}
                {placedSignatures.length > 0 && (
                  <button
                    disabled={isProcessing}
                    onClick={
                      previewPdfUrl ? () => setPreviewPdfUrl(null) : onPreview
                    }
                    style={{
                      ...(previewPdfUrl ? styles.secondaryBtn : styles.successBtn),
                      cursor: isProcessing ? 'not-allowed' : 'pointer',
                    }}
                  >
                    {previewPdfUrl ? (
                      <>
                        <Edit3 size={18} /> Back to Edit
                      </>
                    ) : (
                      <>
                        <Eye size={18} /> Preview
                      </>
                    )}
                  </button>
                )}

                {placedSignatures.length > 0 && !!previewPdfUrl && (
                  <button
                    disabled={isProcessing}
                    onClick={onFinish}
                    style={{
                      ...styles.primaryBtn,
                      opacity: isProcessing ? 0.7 : 1,
                      cursor: isProcessing ? 'not-allowed' : 'pointer',
                    }}
                  >
                    {isProcessing ? (
                      <>
                        <RefreshCw size={18} className="spinner" /> Saving...
                      </>
                    ) : (
                      <>
                        <Save size={18} /> Finish
                      </>
                    )}
                  </button>
                )}
              </div>

              {/* Modal: Create Signature */}
              {isModalOpen && (
                <div style={styles.modalOverlay}>
                  <div style={styles.modalContent}>
                    <div style={styles.modalHeader}>
                      <h3 style={styles.modalTitle}>Create Signature</h3>
                      <button
                        onClick={() => setIsModalOpen(false)}
                        style={styles.closeBtn}
                      >
                        <X size={24} />
                      </button>
                    </div>

                    <div style={styles.tabs}>
                      <button
                        onClick={() => setInputMethod('draw')}
                        style={
                          inputMethod === 'draw' ? styles.activeTab : styles.tab
                        }
                      >
                        <PenTool size={16} /> Draw
                      </button>
                      <button
                        onClick={() => setInputMethod('type')}
                        style={
                          inputMethod === 'type' ? styles.activeTab : styles.tab
                        }
                      >
                        <Type size={16} /> Type
                      </button>
                    </div>

                    <div style={styles.canvasArea}>
                      {inputMethod === 'draw' ? (
                        <SignatureCanvas
                          ref={signaturePadRef}
                          penColor="black"
                          canvasProps={{
                            width: 380,
                            height: 200,
                            className: 'sigCanvas',
                          }}
                        />
                      ) : (
                        <div style={styles.typeArea}>
                          <input
                            type="text"
                            placeholder="Type your name..."
                            value={typedSignatureText}
                            onChange={(e) => setTypedSignatureText(e.target.value)}
                            style={styles.textInput}
                          />
                          <div style={styles.previewText}>
                            {typedSignatureText || 'Preview'}
                          </div>
                        </div>
                      )}
                    </div>

                    <div style={styles.actions}>
                      <button onClick={clearSignaturePad} style={styles.btnDanger}>
                        Clear
                      </button>
                      <button
                        onClick={() => {
                          saveSignature();
                        }}
                        style={styles.btnPrimaryCheck}
                        disabled={isProcessing}
                      >
                        {isProcessing ? (
                          <RefreshCw size={14} className="spinner" />
                        ) : (
                          <CheckCircle size={14} />
                        )}
                        {isProcessing ? 'Saving...' : 'Confirm'}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div style={styles.completedContainer}>
              <div style={styles.completedCard}>
                <CheckCircle
                  size={64}
                  color="#10b981"
                  style={{ marginBottom: '20px' }}
                />
                <h1 style={styles.completedTitle}>Document Signed</h1>
                <p style={styles.completedSubtitle}>
                  You have already signed this document successfully.
                </p>
                {type === 'company' ? (
                  <div style={{ width: '100%', marginTop: '24px' }}>
                    {/* Primary Signed Doc URL */}
                    {(signPDFData?.signed_doc_url) && (
                      <button
                        onClick={() => window.open(signPDFData.signed_doc_url, '_blank')}
                        style={{ ...styles.primaryBtn, width: '100%', marginBottom: '12px', justifyContent: 'center' }}
                      >
                        <Eye size={18} /> View Signed PDF
                      </button>
                    )}

                    {/* Nested Documents List */}
                    {signPDFData?.documents?.map((doc: any, index: number) => {
                      const url = doc.signed_doc_url || doc.doc_url;
                      if (!url) return null;
                      const label = doc.signed_doc_url ? 'Signed Document' : 'Original Document';
                      return (
                        <button
                          key={index}
                          onClick={() => window.open(url, '_blank')}
                          style={{
                            ...(doc.signed_doc_url ? styles.primaryBtn : styles.secondaryBtn),
                            width: '100%',
                            marginBottom: '12px',
                            justifyContent: 'center'
                          }}
                        >
                          <Eye size={18} /> View {label} {index + 1}
                        </button>
                      );
                    })}

                    {/* Single Fallback if no documents list exists and signed_doc_url is missing */}
                    {!signPDFData?.signed_doc_url && (!signPDFData?.documents || signPDFData.documents.length === 0) && signPDFData?.doc_url && (
                      <button
                        onClick={() => window.open(signPDFData.doc_url, '_blank')}
                        style={{ ...styles.secondaryBtn, width: '100%', marginTop: '12px', justifyContent: 'center' }}
                      >
                        <Eye size={18} /> View Document
                      </button>
                    )}
                  </div>
                ) : (
                  signPDFData?.signed_doc_url && (
                    <button
                      onClick={() => window.open(signPDFData.signed_doc_url, '_blank')}
                      style={{
                        ...styles.primaryBtn,
                        marginTop: '24px',
                        width: '100%',
                        justifyContent: 'center'
                      }}
                    >
                      <Eye size={18} /> View Signed PDF
                    </button>
                  )
                )}
              </div>
            </div>
          )}
        </>
      ) : <Loader />}
    </>
  );
};

export default Signdoc;

// --- CSS Styles ---

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    fontFamily: "'Inter', 'Segoe UI', Roboto, sans-serif",
    backgroundColor: '#f8fafc',
    minHeight: '100vh',
    paddingBottom: '100px',
  },
  header: {
    backgroundColor: '#ffffff',
    padding: '24px 32px',
    borderBottom: '1px solid #e2e8f0',
    boxShadow:
      '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
    marginBottom: '32px',
    textAlign: 'center',
  },
  title: {
    margin: 0,
    fontSize: '24px',
    fontWeight: 600,
    color: '#0f172a',
  },
  subtitle: {
    margin: '8px 0 0 0',
    fontSize: '14px',
    color: '#64748b',
  },
  contentWrapper: {
    padding: '0 24px',
  },
  pdfCol: {
    width: '100%',
    maxWidth: '900px',
  },
  pdfWrapper: {
    position: 'relative',
    width: '100%',
    backgroundColor: '#ffffff',
    border: '1px solid #cbd5e1',
    borderRadius: '12px',
    boxShadow:
      '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    overflow: 'hidden',
    height: 'auto',
  },
  iframe: {
    width: '100%',
    height: '100%',
    border: 'none',
    pointerEvents: 'none',
    backgroundColor: '#cbd5e1',
  },
  loaderContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '400px',
    color: '#64748b',
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    border: '1px solid #cbd5e1',
  },
  spinner: {
    animation: 'spin 1s linear infinite',
    marginBottom: '16px',
    color: '#3b82f6',
  },
  actionPanel: {
    position: 'fixed',
    bottom: '32px',
    left: '50%',
    transform: 'translateX(-50%)',
    backgroundColor: '#ffffff',
    padding: '16px 24px',
    borderRadius: '100px',
    boxShadow:
      '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
    display: 'flex',
    gap: '16px',
    zIndex: 100,
    border: '1px solid #e2e8f0',
  },
  primaryBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px 24px',
    backgroundColor: '#2563eb',
    color: '#ffffff',
    border: 'none',
    borderRadius: '100px',
    fontSize: '15px',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.2s',
    boxShadow: '0 4px 6px -1px rgba(37, 99, 235, 0.4)',
  },
  successBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px 24px',
    backgroundColor: '#10b981',
    color: '#ffffff',
    border: 'none',
    borderRadius: '100px',
    fontSize: '15px',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.2s',
    boxShadow: '0 4px 6px -1px rgba(16, 185, 129, 0.4)',
  },
  secondaryBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px 24px',
    backgroundColor: '#f1f5f9',
    color: '#334155',
    border: '1px solid #cbd5e1',
    borderRadius: '100px',
    fontSize: '15px',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    backgroundColor: 'rgba(15, 23, 42, 0.6)',
    backdropFilter: 'blur(4px)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
  },
  modalContent: {
    backgroundColor: '#ffffff',
    padding: '32px',
    borderRadius: '20px',
    width: '90%',
    maxWidth: '480px',
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    animation: 'slideUp 0.3s ease-out',
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '24px',
  },
  modalTitle: {
    margin: 0,
    fontSize: '20px',
    fontWeight: 600,
    color: '#0f172a',
  },
  closeBtn: {
    background: 'transparent',
    border: 'none',
    color: '#94a3b8',
    cursor: 'pointer',
    padding: '4px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '50%',
    transition: 'all 0.2s',
  },
  tabs: {
    display: 'flex',
    gap: '8px',
    marginBottom: '20px',
    backgroundColor: '#f1f5f9',
    padding: '4px',
    borderRadius: '12px',
  },
  tab: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    padding: '12px 16px',
    backgroundColor: 'transparent',
    color: '#64748b',
    border: 'none',
    cursor: 'pointer',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: 600,
    transition: 'all 0.2s',
  },
  activeTab: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    padding: '12px 16px',
    backgroundColor: '#ffffff',
    color: '#2563eb',
    border: 'none',
    cursor: 'pointer',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: 600,
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
  },
  canvasArea: {
    border: '2px dashed #cbd5e1',
    backgroundColor: '#f8fafc',
    borderRadius: '12px',
    minHeight: '220px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    position: 'relative',
    transition: 'border-color 0.2s',
  },
  typeArea: {
    width: '100%',
    padding: '24px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  textInput: {
    width: '100%',
    padding: '16px 20px',
    marginBottom: '24px',
    fontSize: '16px',
    backgroundColor: '#ffffff',
    border: '1px solid #cbd5e1',
    borderRadius: '12px',
    outline: 'none',
    transition: 'border-color 0.2s, box-shadow 0.2s',
    color: '#0f172a',
  },
  previewText: {
    fontFamily: '"Allura", cursive',
    fontSize: '64px',
    minHeight: '100px',
    color: '#0f172a',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    borderBottom: '1px solid #e2e8f0',
    paddingBottom: '16px',
  },
  actions: {
    marginTop: '32px',
    display: 'flex',
    gap: '12px',
    justifyContent: 'flex-end',
  },
  btnPrimaryCheck: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px 28px',
    backgroundColor: '#2563eb',
    color: '#ffffff',
    border: 'none',
    borderRadius: '10px',
    fontSize: '15px',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.2s',
    boxShadow: '0 4px 6px -1px rgba(37, 99, 235, 0.4)',
  },
  btnDanger: {
    padding: '12px 24px',
    backgroundColor: 'transparent',
    color: '#64748b',
    border: '1px solid #cbd5e1',
    borderRadius: '10px',
    fontSize: '15px',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  completedContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100vh',
    backgroundColor: '#f8fafc',
    padding: '24px',
  },
  completedCard: {
    backgroundColor: '#ffffff',
    padding: '48px',
    borderRadius: '24px',
    boxShadow:
      '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
    textAlign: 'center',
    maxWidth: '480px',
    width: '100%',
  },
  completedTitle: {
    margin: '0 0 12px 0',
    fontSize: '28px',
    fontWeight: 700,
    color: '#0f172a',
  },
  completedSubtitle: {
    margin: 0,
    fontSize: '16px',
    color: '#64748b',
    lineHeight: '1.5',
  },
};

const globalStylesText = `
  @keyframes spin {
    100% { transform: rotate(360deg); }
  }
  @keyframes slideUp {
    from { opacity: 0; transform: translateY(20px); }
    to { opacity: 1; transform: translateY(0); }
  }
  .sigCanvas {
    width: 100%;
    height: 100%;
  }
  .spinner {
    animation: spin 1s linear infinite;
    width: 20px;
    height: 20px;
  }
  input:focus {
    border-color: #3b82f6 !important;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.2) !important;
  }
  button:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
  }
  button:disabled {
    opacity: 0.7;
    transform: none;
    box-shadow: none;
  }
`;
